const hasText = value => typeof value === 'string' && value.trim().length > 0;
const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);

// Match full-width characters, letter case and spacing without discarding
// meaningful symbols such as %, + and -. Values are always ordinary text.
export function normalizeTerm(value) {
  return typeof value === 'string' ? value.normalize('NFKC').toLocaleLowerCase('en-US').replace(/\s+/gu, '') : '';
}

/** Build an ordered index while preserving each definition's exact lesson sources. */
export function buildLearningGlossary(lessons) {
  if (!Array.isArray(lessons)) return [];
  const terms = new Map();
  for (const lesson of lessons) {
    if (!isRecord(lesson) || !hasText(lesson.id) || !hasText(lesson.moduleId) || !Array.isArray(lesson.glossary)) continue;
    for (const item of lesson.glossary) {
      if (!isRecord(item) || !hasText(item.term) || !hasText(item.definition)) continue;
      const id = normalizeTerm(item.term);
      if (!id) continue;
      if (!terms.has(id)) {
        terms.set(id, { id, term: item.term.trim(), definitions: [], lessonIds: [], moduleIds: [] });
      }
      const entry = terms.get(id);
      const text = item.definition.trim();
      let definition = entry.definitions.find(candidate => candidate.text === text);
      if (!definition) {
        definition = { text, lessonIds: [] };
        entry.definitions.push(definition);
      }
      if (!definition.lessonIds.includes(lesson.id)) definition.lessonIds.push(lesson.id);
      if (!entry.lessonIds.includes(lesson.id)) entry.lessonIds.push(lesson.id);
      if (!entry.moduleIds.includes(lesson.moduleId)) entry.moduleIds.push(lesson.moduleId);
    }
  }
  return Array.from(terms.values());
}

const validIds = value => Array.isArray(value) && value.length > 0 && value.every(hasText);
const validEntry = entry => isRecord(entry) && hasText(entry.id) && hasText(entry.term)
  && validIds(entry.lessonIds) && validIds(entry.moduleIds)
  && Array.isArray(entry.definitions) && entry.definitions.length > 0
  && entry.definitions.every(definition => isRecord(definition) && hasText(definition.text)
    && validIds(definition.lessonIds) && definition.lessonIds.every(id => entry.lessonIds.includes(id)));

/** Search term or definition text; a module filter further narrows the same results. */
export function searchLearningGlossary(entries, query = '', moduleId = 'all') {
  if (!Array.isArray(entries) || typeof query !== 'string' || !hasText(moduleId)) return [];
  const needle = normalizeTerm(query);
  const module = normalizeTerm(moduleId);
  return entries.filter(entry => validEntry(entry)
    && (module === 'all' || entry.moduleIds.some(id => normalizeTerm(id) === module))
    && (!needle || normalizeTerm(entry.term).includes(needle)
      || entry.definitions.some(definition => normalizeTerm(definition.text).includes(needle))));
}
